import { useEffect, RefObject } from 'react';
import { BetslipCommandType } from 'src/domains/sportsbook/betting/state/multibetState/Types';

interface MessageHandlerOptionsType {
    onAddToBetslip?: (data: BetslipCommandType) => void;
    onNon200Response?: () => void;
}

function isValidOrigin(origin: string): boolean {
    const allowedOrigins = [
        window.location.origin, // Your domain
        'https://gsm-widgets.betstream.betgenius.com',
        'https://gsm-widgets-uat.betstream.betgenius.com',
    ];
    return allowedOrigins.includes(origin);
}

function hasProperty<T extends object, K extends PropertyKey>(obj: T, key: K): obj is T & Record<K, unknown> {
    return key in obj;
}

function isBetslipCommand(data: unknown): data is BetslipCommandType {
    if (typeof data !== 'object' || data === null) {
        return false;
    }

    // Check if command field exists and is valid
    if (!hasProperty(data, 'command')) {
        return false;
    }

    const command = data.command;

    if (command !== 'addToBetslip' && command !== 'non200Response') {
        return false;
    }

    // For addToBetslip command, validate required fields
    if (command === 'addToBetslip') {
        const hasDecimalPrice = hasProperty(data, 'decimalPrice') && data.decimalPrice !== undefined;
        const hasSportsbookFixtureId =
            hasProperty(data, 'sportsbookFixtureId') && data.sportsbookFixtureId !== undefined;
        const hasMarketId = hasProperty(data, 'marketId') || hasProperty(data, 'sportsbookMarketId');

        if (!hasDecimalPrice || !hasSportsbookFixtureId || !hasMarketId) {
            return false;
        }
    }

    return true;
}

interface SportsbookMarketContextType {
    Identifier: string;
    SportsBookFixtureId: string;
    MarketId: string;
    SportsBookId: string;
    SportsBookMarketId: string;
    SportsBookContext: string;
}

function isSportsbookMarketContext(obj: unknown): obj is SportsbookMarketContextType {
    if (typeof obj !== 'object' || obj === null) {
        return false;
    }

    return (
        hasProperty(obj, 'Identifier') &&
        typeof obj.Identifier === 'string' &&
        hasProperty(obj, 'SportsBookFixtureId') &&
        typeof obj.SportsBookFixtureId === 'string' &&
        hasProperty(obj, 'MarketId') &&
        typeof obj.MarketId === 'string' &&
        hasProperty(obj, 'SportsBookId') &&
        typeof obj.SportsBookId === 'string' &&
        hasProperty(obj, 'SportsBookMarketId') &&
        typeof obj.SportsBookMarketId === 'string' &&
        hasProperty(obj, 'SportsBookContext') &&
        typeof obj.SportsBookContext === 'string'
    );
}

function parseWidgetMessage(data: unknown): BetslipCommandType {
    if (!isBetslipCommand(data)) {
        throw new Error('Invalid message format');
    }

    // If this is an addToBetslip command, we need to transform it
    if (data.command === 'addToBetslip') {
        // Parse sportsbookMarketContext if it exists
        let marketContext: SportsbookMarketContextType | null = null;
        if (hasProperty(data, 'sportsbookMarketContext') && typeof data.sportsbookMarketContext === 'string') {
            try {
                const parsed: unknown = JSON.parse(data.sportsbookMarketContext);
                if (isSportsbookMarketContext(parsed)) {
                    marketContext = parsed;
                }
            } catch (e) {
                console.error('[Multibet] Failed to parse sportsbookMarketContext', e);
            }
        }

        // If selections array is not provided, we need to create it from the single market data
        if (!hasProperty(data, 'selections') || !Array.isArray(data.selections)) {
            // This appears to be a single market selection, not a multibet with multiple selections
            // For now, we'll just pass through the data as-is
            // The MultibetState.addToBetslip will need to handle this case
            console.warn('[Multibet] Received single market data, expected selections array');
        }

        return {
            ...data,
            sportsbookMarketContext:
                marketContext === null ? data.sportsbookMarketContext : JSON.stringify(marketContext),
        };
    }

    return data;
}

export function useMultibetMessageHandler(
    iframeRef: RefObject<HTMLIFrameElement>,
    options: MessageHandlerOptionsType
): void {
    useEffect(() => {
        const handleMessage = (event: MessageEvent): void => {
            // Ignore react-devtools-bridge events
            if (event.data?.source === 'react-devtools-bridge') {
                return;
            }

            // Validate origin
            if (!isValidOrigin(event.origin)) {
                console.warn('[Multibet] Invalid message origin:', event.origin);
                return;
            }

            // Parse and validate message
            try {
                console.log('[Multibet] Message received:', event.data);
                const data = parseWidgetMessage(event.data);

                switch (data.command) {
                    case 'addToBetslip':
                        console.info('[Multibet] Add to betslip command received', data);
                        if (options.onAddToBetslip !== undefined) {
                            options.onAddToBetslip(data);
                        }
                        break;
                    case 'non200Response':
                        console.warn('[Multibet] Authentication error received');
                        if (options.onNon200Response !== undefined) {
                            options.onNon200Response();
                        }
                        break;
                    default:
                        console.warn('[Multibet] Unknown command:', data.command);
                }
            } catch (error) {
                console.error('[Multibet] Error handling widget message:', error);
            }
        };

        window.addEventListener('message', handleMessage);
        return (): void => window.removeEventListener('message', handleMessage);
    }, [options, iframeRef]);
}
